import {
	Dispatch,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	Anchor,
	BusyIndicator,
	ContextActionBar,
	FormattedText,
	PageContentTitle,
	SearchInputForm,
	SearchPlaceholder
} from "client/Components";
import { contains } from "client/Data/searching";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useIsMounted,
	useSelection,
	useUnmountedCancellation
} from "client/Hooks";
import {
	BookmarkModel,
	getBookmarks,
	getSharePointModels,
	getWebDavModels as getWebDavModelsFromBookmarkId,
	removeBookmarks,
	RenameBookmarkModel,
	SharePointModels,
	sortBookmarks
} from "client/Api/Documents/Bookmarks/bookmarks";
import { DataSourceSortDirection } from "client/Data/sorting";
import {
	max,
	min,
	sortBy
} from "client_legacy/ndaw/arrayHelpers";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ActionHandlers,
	BookmarkAction,
	BookmarkActionContext,
	getOpenActions,
	getRootActions,
	getSelectionActions
} from "./bookmarkActions";
import { never } from "client_legacy/ndaw/errors";
import {
	isNil,
	isNotNil,
	isNotNilOrEmpty,
	debounce
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { RenameBookmarkDialog } from "./RenameBookmarkDialog";
import { DocumentPageLayout } from "../DocumentPageLayout";
import { IStoreHistoryState } from "client_legacy/Documents/documentStore";
import { BookmarksTableView, BookmarksTableViewHandle } from "./BookmarksTableView";
import { BookmarksListViewHandle, BookmarksListView } from "./BookmarksListView";
import { SelectMode } from "client/Misc/commonTypes";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "client/Features/Documents/useEditorPreference";
import { DocumentStoreType } from "client/Api/Documents/common";
import { downloadDocument } from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import { download } from "client_legacy/ndaw/fileDownloader";
import { getFileDownloadUrl } from "../WebDav/actions";
import { deleteItems } from "client_legacy/Documents/SharePoint/Actions/deleteItemsAction";
import { openManagePermissionsDialog } from "../SharePoint/Dialogs/ManagePermssionsDialog/ManagePermssionsDialog";
import { sendSharePointFileAsAttachment, sendWebDavFileAsAttachment } from "client_legacy/Email/SendFile/sendFile";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { DocumentItemViewModel, DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import {
	canCopy,
	canDelete,
	canDownloadSingleFile,
	canManagePermissions,
	canMoveTo,
	canRename,
	canShareLink,
	canCheckVersionHistory
} from "../SharePoint/actionsRules";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { createRootFolderFromView } from "../SharePoint/SharePointStore";
import { Folder } from "../ItemPicker/WebDavPicker/ItemSelector";
import { createRootFolder, RootFolderId } from "../WebDav/useFolderNavigationStack";
import { useCopyDialog } from "../Dialogs/CopyDialog";
import { useMoveDialog } from "../Dialogs/MoveDialog";
import { getDocumentInformation } from "client/Api/Documents/SharePoint/sharepointDocuments";
import { BookmarkEvent, subscribe } from "client_legacy/Documents/Bookmarks/events";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import { openVersionDialog } from "../SharePoint/Dialogs/VersionDialog";
import { openRenameDialog as openSharePointRenameDialog } from "../SharePoint/Dialogs/RenameDialog";
import { openInviteUserDialog } from "../SharePoint/Dialogs/InviteUserDialog";
import { openShareALinkDialog } from "../SharePoint/Dialogs/ShareLink/ShareALinkDialog";
import { openRenameDialog as openRenameWebDavDialog } from "../WebDav/Dialogs/RenameDialog";
import { getWebDavModel, WebDavModels } from "client/Api/Documents/WebDav/webDav";
import { useDeleteDocumentsDialog } from "../WebDav/Dialogs/DeleteDocumentsDialog";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { BookmarksGridView, BookmarksGridViewHandle } from "./BookmarksGridView";

enum ItemMoveDirection {
	Up = 1,
	Down = 2
}

const useBookmarksDataSource = createLocalDataSource<BookmarkModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) =>
				contains(item.name, searchTerm)
		}
	})
	.getHook();

export type BookmarksPageContentProps = {
	isMobile: boolean;
	onChangeNavigationState: (state: IStoreHistoryState) => void;
	layoutKind: DocumentLayoutKind;
	onLayoutKindChange(kind: DocumentLayoutKind): void;
};

export const BookmarksPageContent = (props: BookmarksPageContentProps): ReactElement => {
	const {
		isMobile,
		onChangeNavigationState,
		layoutKind,
		onLayoutKindChange
	} = props;

	const [isInteractingWithBackend, setIsInteractingWithBackend] = useState<boolean>(false);
	const [isLoadingModels, setIsLoadingModels] = useState<boolean>(true);
	const [bookmarks, setBookmarks] = useState<readonly BookmarkModel[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [selection, setSelection] = useState<readonly BookmarkModel[]>([]);
	const [renameDialogData, setRenameDialogData] = useState<RenameBookmarkModel | null>(null);
	const [selectMode, setSelectMode] = useState<SelectMode>(SelectMode.Disabled);
	const tableViewRef: RefObject<BookmarksTableViewHandle | null> = useRef<BookmarksTableViewHandle>(null);
	const listViewRef: RefObject<BookmarksListViewHandle | null> = useRef<BookmarksListViewHandle>(null);
	const gridViewRef: RefObject<BookmarksGridViewHandle | null> = useRef<BookmarksGridViewHandle>(null);
	const editorPreference: UserEditorPreference = useEditorPreference();

	const isMounted: () => boolean = useIsMounted();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const [copyDialogElement, openCopyDialog] = useCopyDialog();
	const [moveDialogElement, openMoveDialog] = useMoveDialog();
	const [deleteDocumentsDialog, openDeleteDocumentsDialog] = useDeleteDocumentsDialog();

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm);

			tableViewRef.current
				?.resetScrollPosition();

			listViewRef.current
				?.resetScrollPosition();
		},
		[searchTerm]
	);

	usePubSubEvents(setBookmarks);

	const { currentItems } = useBookmarksDataSource(
		bookmarks,
		{
			searchTerm,
			sorting: {
				direction: DataSourceSortDirection.Ascending,
				propertyName: "sortOrder"
			}
		}
	);

	const { selectionFor, multiSelection } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selection,
			setSelectedItems: setSelection,
			hasMemorization: false,
			allItems: bookmarks
		}
	);

	const allPositions = useMemo(
		(): Map<number, number> => {
			const map: Map<number, number> = new Map<number, number>();
			bookmarks.forEach(
				x => map.set(x.id, x.sortOrder)
			);

			return map;
		},
		[bookmarks]
	);

	const actionContext: BookmarkActionContext = useMemo(
		() => ({
			allPositions,
			selection,
			hasASearchTerm: Boolean(searchTerm),
			isMobile,
			isInSelectMode: selectMode !== SelectMode.Disabled
		}),
		[
			allPositions,
			searchTerm,
			selection,
			isMobile,
			selectMode
		]
	);

	const debounceSetSortOrder = useMemo(
		(): (newBookmarks: BookmarkModel[]) => Promise<void> => {
			return debounce(
				async (
					newBookmarks: BookmarkModel[]
				): Promise<void> => {
					try {
						await sortBookmarks(
							newBookmarks.map(
								x => ({
									id: x.id,
									sortOrder: x.sortOrder
								})
							),
							globalCancellation
						);
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						console.error(e);
						alertManager.showError(localize("_GeneralErrorMessage"));
					}
				},
				250
			);
		},
		[
			alertManager,
			globalCancellation
		]
	);

	const removeBookmarksInternalHandler = useCallback(
		async (bookmarkIds: number[]): Promise<void> => {
			await removeBookmarks(bookmarkIds, globalCancellation);
			setBookmarks(
				prevData => {
					return prevData.filter(
						x => !bookmarkIds.includes(x.id)
					);
				}
			);
		},
		[globalCancellation]
	);

	const renderRemoveBookmarkLink = useCallback(
		(bookmarkId: number): ReactElement => {
			return (
				<Anchor onClick={() => removeBookmarksInternalHandler([bookmarkId])}>
					{localize("_Here")}
				</Anchor>
			);
		},
		[removeBookmarksInternalHandler]
	);

	const retrieveSharepointModels = useCallback(
		async (id: number, actionName: string): Promise<SharePointModels> => {
			try {
				setIsInteractingWithBackend(true);
				const model = await getSharePointModels(id, globalCancellation);
				setIsInteractingWithBackend(false);
				return model;
			} catch (e) {
				if (isCancelledError(e)) {
					throw e;
				}

				alertManager.showError(
					<FormattedText
						text={localize("_CantPerformActionNameTheBookmarkPleaseRetryOrDeleteTheBookmark")}
						thingsToReplace={[
							{ what: "{{ActionName}}", filler: actionName },
							{ what: "{{Here}}", filler: renderRemoveBookmarkLink(id) }
						]}
					/>
				);
				setIsInteractingWithBackend(false);
				throw e;
			}

		},
		[
			alertManager,
			globalCancellation,
			renderRemoveBookmarkLink
		]
	);

	const retrieveWebDavModels = useCallback(
		async (id: number, actionName: string):Promise<WebDavModels> => {
			try {
				setIsInteractingWithBackend(true);
				const model = await getWebDavModelsFromBookmarkId(id, globalCancellation);
				setIsInteractingWithBackend(false);
				return model;
			} catch (e) {
				if (isCancelledError(e)) {
					throw e;
				}

				alertManager.showError(
					<FormattedText
						text={localize("_CantPerformActionNameTheBookmarkPleaseRetryOrDeleteTheBookmark")}
						thingsToReplace={[
							{ what: "{{ActionName}}", filler: actionName },
							{ what: "{{Here}}", filler: renderRemoveBookmarkLink(id) }
						]}
					/>
				);
				setIsInteractingWithBackend(false);
				throw e;
			}

		},
		[
			alertManager,
			globalCancellation,
			renderRemoveBookmarkLink
		]
	);

	const guardAgainstInfectedDocument = useCallback(
		(document: DocumentItemViewModel, fn: () => void) => {
			if (document.itemType === DocumentItemType.File && document.isInfected) {
				alertManager.showWarning(
					localize("_ThisFileIsInfectedByMalwareToProtectYourPcYouCannotOpenShareOrDownloadThisFile")
				);
				return;
			}
			fn();
		},
		[alertManager]
	);

	const removeBookmarksHandler = useCallback(
		async(
			bookmarkIds: number[],
			bookmarkNames: string[]
		): Promise<void> => {
			if (isInteractingWithBackend) {
				return;
			}
			setIsInteractingWithBackend(true);
			try {
				await removeBookmarksInternalHandler(bookmarkIds);
				setIsInteractingWithBackend(false);
				alertManager.showSuccess(
					<FormattedText
						text={localize("_BookmarksRemovedForItemNames")}
						thingsToReplace={[
							{
								what: "{ItemNames}",
								filler: bookmarkNames.join(", ")
							}
						]}
					/>
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsInteractingWithBackend(false);
			}
		},
		[
			alertManager,
			isInteractingWithBackend,
			removeBookmarksInternalHandler
		]
	);

	const moveItemOrderHandler = useCallback(
		(
			id: number,
			direction: ItemMoveDirection
		): void => {
			const copy: BookmarkModel[] = [...sortBy(bookmarks, x => x.sortOrder)];
			const indexOfBookmark: number = copy.findIndex(x => x.id === id);
			if (indexOfBookmark === -1) {
				throw new Error(`There is no item with id ${id}`);
			}
			const bookmark: BookmarkModel = copy[indexOfBookmark];
			const allOrders: number[] = copy.map(x => x.sortOrder);
			const fromOrder: number = bookmark.sortOrder;
			const isFirst: boolean = fromOrder === min(allOrders);
			const isLast: boolean = fromOrder === max(allOrders);
			let targetItem: BookmarkModel | undefined;

			switch (direction) {
				case ItemMoveDirection.Up:
					if (!isFirst) {
						targetItem = copy[indexOfBookmark - 1];
					}
					break;
				case ItemMoveDirection.Down:
					if (!isLast) {
						targetItem = copy[indexOfBookmark + 1];
					}
					break;
				default:
					never(direction);
			}

			const toOrder: number | undefined = targetItem?.sortOrder;

			if (isNil(toOrder)) {
				return;
			}

			const result: BookmarkModel[] = copy.map(x => {
				if (x.id === id) {
					return {
						...x,
						sortOrder: toOrder
					};
				}
				if (x.id === targetItem?.id) {
					return {
						...x,
						sortOrder: fromOrder
					};
				}
				return x;
			});

			setBookmarks(result);
			debounceSetSortOrder(result);
		},
		[
			bookmarks,
			debounceSetSortOrder
		]
	);

	const downloadItemHandler = useCallback(
		async (selection: BookmarkModel) => {
			const { storeType } = selection;

			switch (storeType) {
				case DocumentStoreType.SharePoint:
					const sharepointModel: SharePointModels = await retrieveSharepointModels(
						selection.id,
						localize("_Download")
					);

					if (!canDownloadSingleFile([sharepointModel.item])) {
						alertManager.showError(
							localize("_YouDoNotHavePermissionForThat")
						);
						return;
					}

					guardAgainstInfectedDocument(
						sharepointModel.item,
						() => downloadDocument(sharepointModel.item)
					);

					break;
				case DocumentStoreType.WebDav:
					const webDavModel: WebDavModels = await retrieveWebDavModels(
						selection.id,
						localize("_Download")
					);

					download(getFileDownloadUrl(webDavModel.item.id, webDavModel.server.id));
					break;
				default:
					never(storeType);
			}
		},
		[
			alertManager,
			retrieveSharepointModels,
			retrieveWebDavModels,
			guardAgainstInfectedDocument
		]
	);

	const moveItemHandler = useCallback(
		async (selection: BookmarkModel) => {
			const { storeType } = selection;

			switch (storeType) {
				case DocumentStoreType.SharePoint:
					const sharepointModel: SharePointModels = await retrieveSharepointModels(
						selection.id,
						localize("_MoveTo")
					);
					let parentSharePointFolderModel: Folderish;

					if (isNotNil(sharepointModel.item.parentFolderId)) {
						const parentFolder: DocumentItemViewModel = await getDocumentInformation(
							sharepointModel.item.location,
							sharepointModel.item.parentFolderId,
							globalCancellation
						);

						if (parentFolder.itemType !== DocumentItemType.Folder) {
							throw new Error("Folder expected");
						}

						parentSharePointFolderModel = {
							...parentFolder,
							isRoot: false
						};
					} else {
						parentSharePointFolderModel = createRootFolderFromView(sharepointModel.view);
					}

					if (!canMoveTo([sharepointModel.item], sharepointModel.view)) {
						alertManager.showError(
							localize("_YouDoNotHavePermissionForThat")
						);
						return;
					}

					guardAgainstInfectedDocument(
						sharepointModel.item,
						() => {
							openMoveDialog({
								thingsToMove: {
									storeKind: DocumentStoreType.SharePoint,
									source: {
										kind: DocumentStoreType.SharePoint,
										folder: {
											...parentSharePointFolderModel,
											view: sharepointModel.view
										}
									},
									documents: [sharepointModel.item]
								}
							});
						}
					);
					break;
				case DocumentStoreType.WebDav:
					const webDavModel: WebDavModels = await retrieveWebDavModels(
						selection.id,
						localize("_MoveTo")
					);
					let webDavParentFolderModel: Folder;

					if (webDavModel.item.parentId !== RootFolderId) {
						const parentFolder: WebDavModels = await getWebDavModel(
							webDavModel.server.id,
							webDavModel.item.parentId,
							globalCancellation
						);

						webDavParentFolderModel = {
							...parentFolder.item,
							server: webDavModel.server
						};
					} else {
						webDavParentFolderModel = {
							...createRootFolder(webDavModel.server),
							server: webDavModel.server
						};
					}

					openMoveDialog({
						thingsToMove: {
							storeKind: DocumentStoreType.WebDav,
							source: {
								kind: DocumentStoreType.WebDav,
								folder: webDavParentFolderModel
							},
							documents: [webDavModel.item]
						}
					});

					break;
				default:
					never(storeType);
			}
		},
		[
			retrieveSharepointModels,
			guardAgainstInfectedDocument,
			retrieveWebDavModels,
			openMoveDialog,
			globalCancellation,
			alertManager
		]
	);

	const copyItemHandler = useCallback(
		async (selection: BookmarkModel) => {
			const { storeType } = selection;

			switch (storeType) {
				case DocumentStoreType.SharePoint:
					const sharepointModel: SharePointModels = await retrieveSharepointModels(
						selection.id,
						localize("_CopyTo")
					);
					let sharePointParentFolderModel: Folderish;

					if (isNotNil(sharepointModel.item.parentFolderId)) {
						const parentFolder: DocumentItemViewModel = await getDocumentInformation(
							sharepointModel.item.location,
							sharepointModel.item.parentFolderId,
							globalCancellation
						);

						if (parentFolder.itemType !== DocumentItemType.Folder) {
							throw new Error("Folder expected");
						}

						sharePointParentFolderModel = {
							...parentFolder,
							isRoot: false
						};
					} else {
						sharePointParentFolderModel = createRootFolderFromView(sharepointModel.view);
					}

					if (!canCopy([sharepointModel.item], sharepointModel.view)) {
						alertManager.showError(
							localize("_YouDoNotHavePermissionForThat")
						);
						return;
					}

					guardAgainstInfectedDocument(
						sharepointModel.item,
						() => {
							openCopyDialog({
								thingsToCopy: {
								storeKind: DocumentStoreType.SharePoint,
								documents: [sharepointModel.item],
								source: {
									kind: DocumentStoreType.SharePoint,
									folder: {
										...sharePointParentFolderModel,
										view: sharepointModel.view
									}
								}
							}
						});
						}
					);
					break;
				case DocumentStoreType.WebDav:
					const webDavModel: WebDavModels = await retrieveWebDavModels(
						selection.id,
						localize("_CopyTo")
					);
					let webDavParentFolderModel: Folder;

					if (webDavModel.item.parentId !== RootFolderId) {
						const parentFolder: WebDavModels = await getWebDavModel(
							webDavModel.server.id,
							webDavModel.item.parentId,
							globalCancellation
						);

						webDavParentFolderModel = {
							...parentFolder.item,
							server: webDavModel.server
						};
					} else {
						webDavParentFolderModel = {
							...createRootFolder(webDavModel.server),
							server: webDavModel.server
						};
					}

					openCopyDialog({
						thingsToCopy: {
							storeKind: DocumentStoreType.WebDav,
							documents: [webDavModel.item],
							source: {
								kind: DocumentStoreType.WebDav,
								folder: webDavParentFolderModel
							}
						}
					});
					break;
				default:
					never(storeType);
			}
		},
		[
			retrieveSharepointModels,
			guardAgainstInfectedDocument,
			retrieveWebDavModels,
			openCopyDialog,
			globalCancellation,
			alertManager
		]
	);

	const deleteItemHandler = useCallback(
		async (selection: BookmarkModel) => {
			const { storeType } = selection;

			switch (storeType) {
				case DocumentStoreType.SharePoint:
					const sharepointModel: SharePointModels = await retrieveSharepointModels(
						selection.id,
						localize("_Delete")
					);
					if (!canDelete(
						[sharepointModel.item],
						sharepointModel.view,
						// NOTE: we don't have isRootFolder flag on the BookmarkModel we assume it's false
						// for bookmarks, isRootFolder flag doesn't change anything
						false
					)) {
						alertManager.showError(
							localize("_YouDoNotHavePermissionForThat")
						);
						return;
					}
					deleteItems(
						sharepointModel.item
							.location,
						[sharepointModel.item],
						async () => {
							try {
								if (sharepointModel.item.bookmarkId) {
									await removeBookmarksInternalHandler([sharepointModel.item.bookmarkId]);
								}
							} catch {
								alertManager.showError(localize("_GeneralErrorMessage"));
							}
						}
					);
					break;
				case DocumentStoreType.WebDav:
					const webDavModel: WebDavModels = await retrieveWebDavModels(
						selection.id,
						localize("_Delete")
					);
					openDeleteDocumentsDialog({
						serverId: webDavModel.server
							.id,
						documentsToDelete: [webDavModel.item],
						successCallback: async () => {
							try {
								if (webDavModel.item.bookmarkId) {
									await removeBookmarksInternalHandler([webDavModel.item.bookmarkId]);
									alertManager.showSuccess(localize("_FilesDeletedSuccessfully"));
								}
							} catch {
								alertManager.showError(localize("_GeneralErrorMessage"));
							}
						}
					});
					break;
				default:
					never(storeType);
			}
		},
		[
			alertManager,
			retrieveSharepointModels,
			retrieveWebDavModels,
			removeBookmarksInternalHandler,
			openDeleteDocumentsDialog
		]
	);

	const renameItemHandler = useCallback(
		async (selection: BookmarkModel) => {
			const { storeType } = selection;

			switch (storeType) {
				case DocumentStoreType.SharePoint:
					const sharepointModel: SharePointModels = await retrieveSharepointModels(
						selection.id,
						localize("_Rename")
					);
					if (!canRename([sharepointModel.item], sharepointModel.view)) {
						alertManager.showError(
							localize("_YouDoNotHavePermissionForThat")
						);

						return;
					}

					openSharePointRenameDialog({
						item: sharepointModel.item
					});
					break;
				case DocumentStoreType.WebDav:
					const webDavModel: WebDavModels = await retrieveWebDavModels(
						selection.id,
						localize("_Rename")
					);

					openRenameWebDavDialog({
						serverId: webDavModel.server
							.id,
						document: webDavModel.item
					});
					break;
				default:
					never(storeType);
			}
		},
		[
			alertManager,
			retrieveSharepointModels,
			retrieveWebDavModels
		]
	);

	const permissionsChangeHandler = useCallback(
		async (selection: BookmarkModel) => {
			if (selection.storeType !== DocumentStoreType.SharePoint) {
				throw new Error(`Store is not supported ${selection.storeType}`);
			}

			const sharepointModel: SharePointModels = await retrieveSharepointModels(
				selection.id,
				localize("_Permissions")
			);
			if (!canManagePermissions([sharepointModel.item])) {
				alertManager.showError(
					localize("_YouDoNotHavePermissionForThat")
				);

				return;
			}

			openManagePermissionsDialog({
				document: sharepointModel.item
			});
		},
		[alertManager, retrieveSharepointModels]
	);

	const inviteUserHandler = useCallback(
		async (selection: BookmarkModel) => {
			if (selection.storeType !== DocumentStoreType.SharePoint) {
				throw new Error(`Store is not supported ${selection.storeType}`);
			}

			const sharepointModel: SharePointModels = await retrieveSharepointModels(
				selection.id,
				localize("_InviteUser")
			);

			guardAgainstInfectedDocument(
				sharepointModel.item,
				() => {
					openInviteUserDialog({
						document: sharepointModel.item
					});
				}
			);
		},
		[retrieveSharepointModels, guardAgainstInfectedDocument]
	);

	const shareLinkHandler = useCallback(
		async (selection: BookmarkModel) => {
			if (selection.storeType !== DocumentStoreType.SharePoint) {
				throw new Error(`Store is not supported ${selection.storeType}`);
			}

			const sharepointModel: SharePointModels = await retrieveSharepointModels(
				selection.id,
				localize("_ShareLink")
			);

			const { isSharingViaLinkEnabled } = documentsConfiguration().sharePoint;

			if (!canShareLink(
				[sharepointModel.item],
				sharepointModel.view,
				isSharingViaLinkEnabled
			)) {
				alertManager.showError(
					localize("_NoPermissionsToInviteUserMessage")
				);
				return;
			}

			guardAgainstInfectedDocument(
				sharepointModel.item,
				() => {
					openShareALinkDialog({ document: sharepointModel.item });
				}
			);

		},
		[
			alertManager,
			retrieveSharepointModels,
			guardAgainstInfectedDocument
		]
	);

	const versionHistoryOpenHandler = useCallback(
		async (selection: BookmarkModel) => {
			if (selection.storeType !== DocumentStoreType.SharePoint) {
				throw new Error(`Store is not supported ${selection.storeType}`);
			}

			const sharepointModel: SharePointModels = await retrieveSharepointModels(
				selection.id,
				localize("_VersionHistory")
			);
			if (sharepointModel.item.itemType !== DocumentItemType.File) {
				throw new Error("DocumentItemType not supported");
			}
			const { isVersioningEnabled } = sharepointModel.view;

			if (!canCheckVersionHistory([sharepointModel.item], isVersioningEnabled)) {
				alertManager.showError(
					localize("_NoPermissionsToInviteUserMessage")
				);
				return;
			}

			openVersionDialog({
				item: sharepointModel.item
			});
		},
		[retrieveSharepointModels, alertManager]
	);

	const attachToEmailHandler = useCallback(
		async (selection: BookmarkModel) => {
			const emailClient: EmailClient = getEffectiveEmailClient();
			if (!emailClient.supportsEditDraft) {
				throw new Error("Current email app launcher does not support edit drafts.");
			}

			const { storeType } = selection;

			switch (storeType) {
				case DocumentStoreType.SharePoint:
					const sharepointModel: SharePointModels = await retrieveSharepointModels(
						selection.id,
						localize("_AttachToEmail")
					);

					guardAgainstInfectedDocument(
						sharepointModel.item,
						() => {
							sendSharePointFileAsAttachment(
								sharepointModel.item.id,
								convertToLocationModel(sharepointModel.item.location),
								emailClient
							);
						}
					);
					break;
				case DocumentStoreType.WebDav:
					const webDavModel: WebDavModels = await retrieveWebDavModels(
						selection.id,
						localize("_AttachToEmail")
					);
					sendWebDavFileAsAttachment(
						webDavModel.server.id,
						webDavModel.item.id,
						emailClient
					);
					break;
				default:
					never(storeType);
			}


		},
		[
			retrieveSharepointModels,
			retrieveWebDavModels,
			guardAgainstInfectedDocument
		]
	);

	const changeIsInteractingWithBackendHandler = useCallback(
		(status: boolean) => {
			if(isMounted()){
				setIsInteractingWithBackend(status);
			}
		},
		[isMounted]
	);

	const actionHandlers = useMemo(
		(): ActionHandlers => {
			return {
				onOpenFolderRequestStart: () => changeIsInteractingWithBackendHandler(true),
				onOpenFolderRequestEnd: () => changeIsInteractingWithBackendHandler(false),
				onOpenFileRequestStart: () => changeIsInteractingWithBackendHandler(true),
				onOpenFileRequestEnd: () => changeIsInteractingWithBackendHandler(false),
				onMoveUp: (id: number) => moveItemOrderHandler(id, ItemMoveDirection.Up),
				onMoveDown: (id: number) => moveItemOrderHandler(id, ItemMoveDirection.Down),
				onDownloadBookmark: downloadItemHandler,
				onMoveItem: moveItemHandler,
				onCopyItem: copyItemHandler,
				onDeleteItem: deleteItemHandler,
				onRenameItem: renameItemHandler,
				onRenameBookmark: setRenameDialogData,
				onRemoveBookmarks: removeBookmarksHandler,
				onChangeNavigationState,
				enableSelectMode: () => setSelectMode(SelectMode.Manual),
				disableSelectMode: () => setSelectMode(SelectMode.Disabled),
				selectAll: () => setSelection(currentItems),
				deselectAll: () => setSelection([]),
				onPermissionsChange: permissionsChangeHandler,
				onInviteUser: inviteUserHandler,
				onAttachToEmail: attachToEmailHandler,
				onShareLink: shareLinkHandler,
				onVersionHistoryOpen: versionHistoryOpenHandler
			};
		},
		[
			changeIsInteractingWithBackendHandler,
			copyItemHandler,
			currentItems,
			deleteItemHandler,
			downloadItemHandler,
			inviteUserHandler,
			moveItemHandler,
			moveItemOrderHandler,
			onChangeNavigationState,
			permissionsChangeHandler,
			removeBookmarksHandler,
			renameItemHandler,
			attachToEmailHandler,
			shareLinkHandler,
			versionHistoryOpenHandler
		]
	);

	const selectionActions: BookmarkAction[] = useMemo(
		() => getSelectionActions(
			selection,
			actionHandlers,
			alertManager,
			editorPreference
		),
		[
			selection,
			actionHandlers,
			alertManager,
			editorPreference
		]
	);

	const rootActions = useMemo(
		(): readonly BookmarkAction[] => {
			return getRootActions(
				currentItems,
				actionHandlers
			);
		},
		[
			currentItems,
			actionHandlers
		]
	);

	useEffect(
		() => {
			async function fetchItems(signal: AbortSignal): Promise<void> {
				setIsLoadingModels(true);
				try {
					const models: BookmarkModel[] = await getBookmarks(signal);
					setBookmarks(models);
					setIsLoadingModels(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsLoadingModels(false);
				}
			}

			const cancellation = new AbortController();
			fetchItems(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const openItemHandler = useCallback(
		(bookmark: BookmarkModel): void => {
			const context: BookmarkActionContext = {
				...actionContext,
				selection: [bookmark]
			};

			const actions: readonly BookmarkAction[] = getOpenActions(
				context.selection,
				actionHandlers,
				alertManager,
				editorPreference
			);
			const action: BookmarkAction[] = actions.filter(x => x.canExecute(context));
			if (action[0]?.execute) {
				action[0].execute(context);
			}
		},
		[
			actionContext,
			actionHandlers,
			alertManager,
			editorPreference
		]
	);

	const renamedHandler = (
		id: number,
		newName: string
	): void => {
		setBookmarks(
			prevData => {
				return prevData.map(
					x => {
						if (x.id === id) {
							return {
								...x,
								name: newName
							};
						}
						return x;
					}
				);
			}
		);
	};

	const allActions = useMemo(
		(): readonly BookmarkAction[] => [...rootActions, ...selectionActions],
		[rootActions, selectionActions]
	);

	useEffect(
		() => {
			if (selectMode === SelectMode.FromLongTouch && selection.length === 0) {
				setSelectMode(SelectMode.Disabled);
			}
		},
		[
			selectMode,
			selection.length,
			setSelectMode
		]
	);

	useEffect(
		(): void => {
			if (selectMode === SelectMode.Disabled
				&& isMobile
			) {
				setSelection([]);
			}
		},
		[selectMode, isMobile]
	);

	const renderContent = (): ReactElement => {
		if (!isMobile && layoutKind === DocumentLayoutKind.List) {
			return (
				<BookmarksTableView
					ref={tableViewRef}
					isBusy={isLoadingModels}
					multiSelection={multiSelection}
					selectionFor={selectionFor}
					items={currentItems}
					onOpenItem={openItemHandler}
					actionContext={actionContext}
					selectionActions={selectionActions}
					hasSearch={isNotNilOrEmpty(searchTerm)}
				/>
			);
		}

		if (layoutKind === DocumentLayoutKind.Grid) {
			return (
				<BookmarksGridView
					ref={gridViewRef}
					isBusy={isLoadingModels}
					multiSelection={multiSelection}
					selectionFor={selectionFor}
					items={currentItems}
					onOpenItem={openItemHandler}
					actionContext={actionContext}
					selectionActions={selectionActions}
					isInSelectMode={selectMode !== SelectMode.Disabled}
					onEnableSelectMode={() => setSelectMode(SelectMode.FromLongTouch)}
					hasSearch={isNotNilOrEmpty(searchTerm)}
				/>
			);
		}

		return (
			<BookmarksListView
				ref={listViewRef}
				isBusy={isLoadingModels}
				selectionFor={selectionFor}
				items={currentItems}
				isInSelectMode={selectMode !== SelectMode.Disabled}
				onEnableSelectMode={() => setSelectMode(SelectMode.FromLongTouch)}
				onOpenItem={openItemHandler}
				actionContext={actionContext}
				selectionActions={selectionActions}
				hasSearch={isNotNilOrEmpty(searchTerm)}
			/>
		);
	};

	const shouldShowActionBar = useMemo(
		(): boolean => {
			if (isMobile) {
				return true;
			}

			return selection.length > 0;
		},
		[isMobile, selection.length]
	);

	return (
		<DocumentPageLayout trackingId="bookmarks-page-content">
			<DocumentPageLayout.Header>
				{isMobile && (
					<DocumentPageLayout.BreadCrumb>
						<h3>{localize("_Bookmarks")}</h3>
					</DocumentPageLayout.BreadCrumb>
				)}
				{shouldShowActionBar
					? (
						<DocumentPageLayout.ActionBarContainer>
							<ContextActionBar
								actions={allActions}
								context={actionContext}
								trackingId="global-action-bar"
								horizontalAlignment={isMobile ? "default" : "page-content"}
								hasBorderBottom={isMobile}
							/>
						</DocumentPageLayout.ActionBarContainer>
					)
					: (
						<PageContentTitle
							title={localize("_Bookmarks")}
						/>
					)
				}
				<DocumentPageLayout.SortAndFilter>
					<SearchInputForm
						onChange={setSearchTerm}
						trackingId="search-term"
						placeholder={
							<SearchPlaceholder
								kind="search"
								properties={[localize("_Name")]}
							/>
						}
						height="large"
					/>
					<DocumentPageLayout.LayoutKindChangeButton
						layoutKind={layoutKind}
						onLayoutKindChange={onLayoutKindChange}
					/>
				</DocumentPageLayout.SortAndFilter>
			</DocumentPageLayout.Header>

			<DocumentPageLayout.Content>
				{isInteractingWithBackend && (
					<BusyIndicator />
				)}

				{renderContent()}
			</DocumentPageLayout.Content>

			{renameDialogData && (
				<RenameBookmarkDialog
					id={renameDialogData.id}
					initialName={renameDialogData.name}
					onRenamed={renamedHandler}
					onClose={() => setRenameDialogData(null)}
				/>
			)}

			{copyDialogElement}
			{moveDialogElement}
			{deleteDocumentsDialog}
		</DocumentPageLayout>
	);
};

BookmarksPageContent.displayName = "BookmarksPageContent";

function usePubSubEvents(
	setAllBookmarks: Dispatch<SetStateAction<readonly BookmarkModel[]>>
): void {
	useEffect(
		(): (() => void) => {
			const subscriptionTokens: SubscriptionTokenCollection = registerSubscriptions(
				setAllBookmarks
			);

			return () => subscriptionTokens.destroy();
		},
		[
			setAllBookmarks
		]
	);
}

function registerSubscriptions(
	setAllBookmarks: Dispatch<SetStateAction<readonly BookmarkModel[]>>
): SubscriptionTokenCollection {
	const result = new SubscriptionTokenCollection();

	// We need this subscription because of "removeBookmarksWithAlert" in "opener.tsx"
	const deletedToken: PubSubSubscriptionToken = subscribe(
		BookmarkEvent.Deleted,
		(_, bookmarkIds: number[]) => {
			setAllBookmarks(
				prevState => prevState
					.filter(
						x => !bookmarkIds.includes(x.id)
					)
			);
		}
	);

	result.add(deletedToken);

	return result;
}
